import type { CallToolResult } from '@modelcontextprotocol/sdk/types.js';
import { Field, Fields, type IFIXParser, type Message, Messages } from 'fixparser';
import type { VerifiedOrder } from '../schemas';

const ordTypeNames: Record<string, string> = {
    '1': 'Market',
    '2': 'Limit',
    '3': 'Stop',
    '4': 'StopLimit',
    '5': 'MarketOnClose',
    '6': 'WithOrWithout',
    '7': 'LimitOrBetter',
    '8': 'LimitWithOrWithout',
    '9': 'OnBasis',
    A: 'OnClose',
    B: 'LimitOnClose',
    C: 'ForexMarket',
    D: 'PreviouslyQuoted',
    E: 'PreviouslyIndicated',
    F: 'ForexLimit',
    G: 'ForexSwap',
    H: 'ForexPreviouslyQuoted',
    I: 'Funari',
    J: 'MarketIfTouched',
    K: 'MarketWithLeftOverAsLimit',
    L: 'PreviousFundValuationPoint',
    M: 'NextFundValuationPoint',
    P: 'Pegged',
    Q: 'CounterOrderSelection',
    R: 'StopOnBidOrOffer',
    S: 'StopLimitOnBidOrOffer',
};

const sideNames: Record<string, string> = {
    '1': 'Buy',
    '2': 'Sell',
    '3': 'BuyMinus',
    '4': 'SellPlus',
    '5': 'SellShort',
    '6': 'SellShortExempt',
    '7': 'Undisclosed',
    '8': 'Cross',
    '9': 'CrossShort',
    A: 'CrossShortExempt',
    B: 'AsDefined',
    C: 'Opposite',
    D: 'Subscribe',
    E: 'Redeem',
    F: 'Lend',
    G: 'Borrow',
    H: 'SellUndisclosed',
};

const timeInForceNames: Record<string, string> = {
    '0': 'Day',
    '1': 'GoodTillCancel',
    '2': 'AtTheOpening',
    '3': 'ImmediateOrCancel',
    '4': 'FillOrKill',
    '5': 'GoodTillCrossing',
    '6': 'GoodTillDate',
    '7': 'AtTheClose',
    '8': 'GoodThroughCrossing',
    '9': 'AtCrossing',
    A: 'GoodForTime',
    B: 'GoodForAuction',
    C: 'GoodForMonth',
};

const handlInstNames: Record<string, string> = {
    '1': 'AutomatedExecutionNoIntervention',
    '2': 'AutomatedExecutionInterventionOK',
    '3': 'ManualOrder',
};

export const createVerifyOrderHandler = (
    parser: IFIXParser,
    verifiedOrders: Map<string, VerifiedOrder>,
): ((args: VerifiedOrder) => Promise<CallToolResult>) => {
    return async (args: VerifiedOrder): Promise<CallToolResult> => {
        void parser; // Parser unused for now
        try {
            // Store the verified order parameters
            verifiedOrders.set(args.clOrdID, {
                clOrdID: args.clOrdID,
                handlInst: args.handlInst,
                quantity: Number.parseFloat(String(args.quantity)),
                price: Number.parseFloat(String(args.price)),
                ordType: args.ordType,
                side: args.side,
                symbol: args.symbol,
                timeInForce: args.timeInForce,
            });

            return {
                content: [
                    {
                        type: 'text',
                        text: `VERIFICATION: All parameters valid. Ready to proceed with order execution.
                            
Parameters verified:
- ClOrdID: ${args.clOrdID}
- HandlInst: ${args.handlInst} (${handlInstNames[args.handlInst]})
- Quantity: ${args.quantity}
- Price: ${args.price}
- OrdType: ${args.ordType} (${ordTypeNames[args.ordType]})
- Side: ${args.side} (${sideNames[args.side]})
- Symbol: ${args.symbol}
- TimeInForce: ${args.timeInForce} (${timeInForceNames[args.timeInForce]})

To execute this order, call the executeOrder tool with these exact same parameters. Important: The user has to explicitly confirm before executeOrder is called!`,
                        uri: 'verifyOrder',
                    },
                ],
            };
        } catch (error) {
            return {
                content: [
                    {
                        type: 'text',
                        text: `Error: ${error instanceof Error ? error.message : 'Failed to verify order parameters'}`,
                        uri: 'verifyOrder',
                    },
                ],
                isError: true,
            };
        }
    };
};

export const createExecuteOrderHandler = (
    parser: IFIXParser,
    verifiedOrders: Map<string, VerifiedOrder>,
    pendingRequests: Map<string, (data: Message) => void>,
): ((args: VerifiedOrder) => Promise<CallToolResult>) => {
    return async (args: VerifiedOrder): Promise<CallToolResult> => {
        try {
            // Check if this order was previously verified
            const verifiedOrder = verifiedOrders.get(args.clOrdID);
            if (!verifiedOrder) {
                return {
                    content: [
                        {
                            type: 'text',
                            text: `Error: Order ${args.clOrdID} has not been verified. Please call verifyOrder first.`,
                            uri: 'executeOrder',
                        },
                    ],
                    isError: true,
                };
            }

            // Verify that all parameters match the verified order
            if (
                verifiedOrder.handlInst !== args.handlInst ||
                verifiedOrder.quantity !== Number.parseFloat(String(args.quantity)) ||
                verifiedOrder.price !== Number.parseFloat(String(args.price)) ||
                verifiedOrder.ordType !== args.ordType ||
                verifiedOrder.side !== args.side ||
                verifiedOrder.symbol !== args.symbol ||
                verifiedOrder.timeInForce !== args.timeInForce
            ) {
                return {
                    content: [
                        {
                            type: 'text',
                            text: 'Error: Order parameters do not match the verified order. Please use the exact same parameters that were verified.',
                            uri: 'executeOrder',
                        },
                    ],
                    isError: true,
                };
            }

            const response = new Promise((resolve) => {
                pendingRequests.set(args.clOrdID, resolve);
            });

            const order: Message | undefined = parser.createMessage(
                new Field(Fields.MsgType, Messages.NewOrderSingle),
                new Field(Fields.MsgSeqNum, parser.getNextTargetMsgSeqNum()),
                new Field(Fields.SenderCompID, parser.sender),
                new Field(Fields.TargetCompID, parser.target),
                new Field(Fields.SendingTime, parser.getTimestamp()),
                new Field(Fields.ClOrdID, args.clOrdID),
                new Field(Fields.Side, args.side),
                new Field(Fields.Symbol, args.symbol),
                new Field(Fields.OrderQty, Number.parseFloat(String(args.quantity))),
                new Field(Fields.Price, Number.parseFloat(String(args.price))),
                new Field(Fields.OrdType, args.ordType),
                new Field(Fields.HandlInst, args.handlInst),
                new Field(Fields.TimeInForce, args.timeInForce),
                new Field(Fields.TransactTime, parser.getTimestamp()),
            );

            if (!parser.connected) {
                return {
                    content: [
                        {
                            type: 'text',
                            text: 'Error: Not connected. Ignoring message.',
                            uri: 'executeOrder',
                        },
                    ],
                    isError: true,
                };
            }

            parser.send(order!);

            const fixData = await response;

            // Remove the verified order after execution
            verifiedOrders.delete(args.clOrdID);

            return {
                content: [
                    {
                        type: 'text',
                        text:
                            (fixData as Message).messageType === Messages.Reject
                                ? `Reject message for order ${args.clOrdID}: ${JSON.stringify((fixData as Message).toFIXJSON())}`
                                : `Execution Report for order ${args.clOrdID}: ${JSON.stringify((fixData as Message).toFIXJSON())}`,
                        uri: 'executeOrder',
                    },
                ],
            };
        } catch (error) {
            return {
                content: [
                    {
                        type: 'text',
                        text: `Error: ${error instanceof Error ? error.message : 'Failed to execute order'}`,
                        uri: 'executeOrder',
                    },
                ],
                isError: true,
            };
        }
    };
};
